import { ReactElement, useState } from 'react';

import styled from 'styled-components';

import { IconProps } from '@components/icons';
import { IconCloseRegular } from '@components/icons/close-regular';
import { Stack } from '@components/stack';
import { Font, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { Text } from '../text';

export interface InfoBoxProps {
  variant?: 'blue' | 'red' | 'orange' | 'green' | 'indigo';
  text?: string;
  font?: Font;
  icon?: ReactElement<IconProps>;
  children?: ReactElement;
  header?: string;
  closable?: boolean;
  onClose?: () => void;
}

export function InfoBox(props: InfoBoxProps) {
  const {
    text,
    variant = 'blue',
    font = 'regular16',
    icon,
    children,
    header,
    closable,
    onClose,
  } = props;
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Container data-variant={variant}>
      <Stack space={2} orientation="horizontal">
        {icon}
        <div>
          {header && <Text font={font} text={header} />}
          {text ? <Text text={text} font={font} /> : children}
        </div>
        {closable && (
          <StyledCloseButtonContainer data-variant={variant}>
            <IconCloseRegular onClick={handleClose} />
          </StyledCloseButtonContainer>
        )}
      </Stack>
    </Container>
  );
}

export const Container = styled.div({
  padding: pxToRem(12),
  borderRadius: pxToRem(4),
  '&[data-variant=blue]': {
    backgroundColor: theme.color.blueTrans5,
    '--icon-color': theme.color.elementBlue100,
    span: {
      color: theme.color.elementBlue100,
    },
  },
  '&[data-variant=red]': {
    backgroundColor: theme.color.redTrans10,
    '--icon-color': theme.color.elementRed200,
    span: {
      color: theme.color.elementRed200,
    },
  },
  '&[data-variant=orange]': {
    backgroundColor: theme.color.orangeTrans10,
    '--icon-color': theme.color.elementOrange200,
    span: {
      color: theme.color.elementOrange200,
    },
  },
  '&[data-variant=green]': {
    backgroundColor: theme.color.greenTrans5,
    '--icon-color': theme.color.elementGreen200,
    span: {
      color: theme.color.elementGreen200,
    },
  },
  '&[data-variant=indigo]': {
    backgroundColor: theme.color.indigoTrans5,
    '--icon-color': theme.color.elementIndigo200,
    span: {
      color: theme.color.elementIndigo100,
    },
  },
});

const StyledCloseButtonContainer = styled.div({
  cursor: 'pointer',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
  '&[data-variant="green"]': {
    color: theme.color.green80,
    ':hover': {
      color: theme.color.elementGreen200,
    },
  },
  '&[data-variant="blue"]': {
    color: theme.color.blue80,
    ':hover': {
      color: theme.color.elementBlue200,
    },
  },
  '&[data-variant="indigo"]': {
    color: theme.color.indigo80,
    ':hover': {
      color: theme.color.elementIndigo200,
    },
  },
  '&[data-variant="orange"]': {
    color: theme.color.orange80,
    ':hover': {
      color: theme.color.elementOrange200,
    },
  },
});
